/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module bulb-image/bulb-image-rotate
 */

import { Plugin, ButtonView } from 'ckeditor5';

import bulbRotateIcon from '../../../assets/media_toolbar_icons/rotate_image_icon.svg';
import { getDisplayedImageAsset } from '../bulbimageutils';
import { windowsEventListenerChecklist } from '../../utils';

/**
 * The image rotate plugin class.
 *
 * @extends Plugin
 */
export default class BulbImageRotateUI extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbImageRotateUI' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { t } = editor;
    let activeImageCarouselSlideIndex: number;

    // The "simpleBox" button must be registered among the UI components of the editor
    // to be displayed in the toolbar.
    editor.ui.componentFactory.add('bulbRotateImage', (locale) => {
      // The state of the button will be bound to the widget command.
      const command = editor.commands.get('rotateBulbImage');

      // The button will be an instance of ButtonView.
      const buttonView = new ButtonView(locale);

      buttonView.set({
        label: t('editor_image_rotate'),
        icon: bulbRotateIcon,
        tooltip: true,
      });

      // Execute command.
      buttonView.on('execute', (event: any) => {
        const blockId = editor.model.document.selection.getSelectedElement()?.getAttribute('bulb-block-id') as string;
        const contentBlock = CKEBulbData.page.unit.contentBlocks[blockId];
        const currentIndexEvent = new CustomEvent('currentSlideIndex', {
          detail: {
            blockId,
          },
        });
        window.dispatchEvent(currentIndexEvent);

        const eventObj = {
          detail: {
            event,
            eventFiredFromEditor: true,
            blockId,
            editor,
          },
        };

        if (contentBlock.blockType === 'IMAGE_CAROUSEL') {
          Object.assign(eventObj.detail, { imageIndex: activeImageCarouselSlideIndex });
        }

        const imageRotateEvent = new CustomEvent('bulbRotateImage', eventObj);
        window.dispatchEvent(imageRotateEvent);
      });

      window.addEventListener('dispatchSlideIndex', (event: any) => {
        activeImageCarouselSlideIndex = event.detail.activeIndex;
        if (event.detail.activeIndex === -1) {
          activeImageCarouselSlideIndex = 0;
        }
      });

      // We have to run this check here due to the re-initialization of the lightSlider that we do in the command
      // execution. By adding this here, the event listener is only added once to the window instead of multiple
      // times due to the way CKEditor's plugin structure works via ckeditor.js.
      if (!windowsEventListenerChecklist.imageRotateAdded) {
        windowsEventListenerChecklist.imageRotateAdded = true;

        // Listen for the event that is going to be fired from our client that the upload was successful.
        window.addEventListener('imageRotationComplete', this.handleRotateImage, false);
      }

      // Bind the state of the button to the command.
      // @ts-ignore
      buttonView.bind('isOn', 'isEnabled').to(command, 'value', 'isEnabled');

      return buttonView;
    });
  }

  /**
   * @inheritDoc
   */
  override destroy() {
    super.destroy();
    window.removeEventListener('imageRotationComplete', this.handleRotateImage, false);
    windowsEventListenerChecklist.imageRotateAdded = false;
  }

  /**
   * Handle image rotation.
   * @param event Event which will contains details of image rotation.
   */
  handleRotateImage(event: CustomEvent): void {
    const { contentBlock } = event.detail;
    const { currentEditor } = event.detail;
    if (contentBlock.blockType === 'IMAGE') {
      const rotatedImageAsset = getDisplayedImageAsset(contentBlock);
      currentEditor.execute('rotateBulbImage', rotatedImageAsset, null);
    } else {
      const customSlideAddedEvent = new CustomEvent('updateCarousel', {
        detail: {
          contentBlock,
        },
      });
      window.dispatchEvent(customSlideAddedEvent);
    }
  }
}
